<template>
  <v-layout column>
    <v-layout>
      <div class="title">Collect</div>
    </v-layout>
    <v-layout class="mb-4" align-center wrap>
      <v-flex xs12>
        <v-switch
          label="Require only one signature from each section"
          v-model="requireOneSignaturePerSection"
          @change="updatePreferences"
          hide-details
        />
      </v-flex>
      <v-flex xs12>
        <v-switch
          label="Disable 'Provided Elsewhere' option"
          v-model="disableProvidedElsewhere"
          @change="updatePreferences"
          hide-details
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <div class="title">Deliver</div>
    </v-layout>
    <v-layout class="mb-4">
      <v-flex xs12>
        <v-switch
          label="Show filing instructions to clients"
          v-model="showFilingInstructionsToClients"
          @change="updatePreferences"
          hide-details
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <div class="title">Documents</div>
    </v-layout>
    <v-layout class="mb-4" align-center wrap>
      <v-flex xs12>
        <v-switch
          label="Disable reupload notification on blurry document uploads"
          v-model="disableReuploadOnLowConfidence"
          @change="updatePreferences"
          hide-details
        />
      </v-flex>
      <v-flex xs12>
        <v-switch
          label="Disable folder actions for clients"
          v-model="disableFolderAction"
          @change="updatePreferences"
          hide-details
        />
      </v-flex>
      <v-flex xs12>
        <v-switch
          label="Enable Questionnaire-Document Notification"
          v-model="enableQuestionnaireDocumentNotification"
          @change="updatePreferences"
          hide-details
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <div class="title">Messaging</div>
    </v-layout>
    <v-layout class="mb-4">
      <v-flex xs12>
        <v-switch
          label="Hide conversation content in email"
          v-model="hideConversationInEmail"
          @change="updatePreferences"
          hide-details
        />
      </v-flex>
    </v-layout>
    <v-layout align-center wrap>
      <div class="title">Client Dashboard Buttons</div>
    </v-layout>
    <v-layout>
      <v-flex xs12>
        <v-layout class="pt-3">
          <v-flex xs4>
            <v-text-field
              solo
              label="Button text"
              v-model="$v.buttonLabel.$model"
              :error="$v.buttonLabel.$error"
              :error-messages="$v.buttonLabel.$error ? ['Required field'] : []"
              class="default-input"
            />
          </v-flex>
          <v-flex xs6>
            <v-text-field
              solo
              label="URL"
              v-model="$v.buttonUrl.$model"
              :error="$v.buttonUrl.$error"
              :error-messages="$v.buttonUrl.$error ? computedUrlError : []"
              class="default-input pl-2"
            />
          </v-flex>
          <v-flex xs2>
            <v-btn
              class="ma-0 default-input primary text-capitalize"
              :disabled="$v.$anyError || loading"
              :loading="loading"
              @click="addButton"
            >
              Add
            </v-btn>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-layout style="min-height: 300px">
      <v-flex xs12>
        <v-list>
          <v-list-tile v-for="(item, idx) in items" :key="idx">
            <v-list-tile-content v-html="item" style="max-width: 272px" />

            <v-list-tile-action>
              <v-icon
                slot="activator"
                class="error--text"
                style="cursor: pointer; padding-top: 10px"
                @click="deleteItem(idx)"
                >delete</v-icon
              >
            </v-list-tile-action>
          </v-list-tile>
        </v-list>
      </v-flex>
    </v-layout>
  </v-layout>
</template>

<script>
import API from '@/api';
import get from 'lodash/get';
import { url, required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';

export default {
  name: 'PersonalizationTab',
  props: {
    firm: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      editableFirm: null,
      buttonLabel: null,
      buttonUrl: null,
      loading: false,
      items: [],
      showFilingInstructionsToClients: false,
      requireOneSignaturePerSection: false,
      disableFolderAction: false,
      enableQuestionnaireDocumentNotification: false,
      disableProvidedElsewhere: false,
      hideConversationInEmail: false,
    };
  },
  watch: {
    firm() {
      this.initList();
    },
  },
  validations: {
    buttonUrl: {
      required,
      url,
    },
    buttonLabel: {
      required,
    },
  },
  computed: {
    computedUrlError() {
      if (!this.$v.buttonUrl.required) {
        return ['URL is required'];
      } else if (!this.$v.buttonUrl.url) {
        return ['Is not a valid URL'];
      }
      return [];
    },
  },
  methods: {
    ...mapActions(['updateCurrentFirm']),
    async addButton() {
      this.loading = true;
      await this.$v.$touch();

      if (!this.$v.$anyError) {
        let url = this.buttonUrl;
        let text = this.buttonLabel;

        let buttonHtml = `<a href="${url}"
         target="blank"
         class="mt-3 mx-0 text-none v-btn v-btn--block v-btn--outline v-btn--depressed theme--light primary--text">
          ${text}
        </a>`;

        this.buttonUrl = null;
        this.buttonLabel = null;
        this.$v.$reset();
        this.items.push(buttonHtml);

        await this.updatePreferences();
        this.loading = false;
      }
    },
    initList() {
      if (this.firm) {
        this.editableFirm = { ...this.firm };

        if (get(this.editableFirm, 'preferences.customElements', null)) {
          const elementsHtml = this.editableFirm.preferences.customElements;
          this.items = elementsHtml
            .split('</a>')
            .filter(item => !!item)
            .map(item => item + '</a>');

          console.log(this.items);
        }
        this.showFilingInstructionsToClients = get(
          this.editableFirm,
          'preferences.show_filing_instructions_to_clients',
          false,
        );
        this.requireOneSignaturePerSection = get(
          this.editableFirm,
          'preferences.require_one_signature_per_section',
          false,
        );
        this.disableProvidedElsewhere = get(
          this.editableFirm,
          'preferences.disable_provided_elsewhere',
          false,
        );
        this.disableFolderAction = get(
          this.editableFirm,
          'preferences.disable_folder_action',
          false,
        );
        this.enableQuestionnaireDocumentNotification = get(
          this.editableFirm,
          'preferences.questionnaire_document_notification_enabled',
          false,
        );
        this.disableReuploadOnLowConfidence = get(
          this.editableFirm,
          'preferences.disable_reupload_on_low_confidence',
          false,
        );
        this.hideConversationInEmail = get(
          this.editableFirm,
          'preferences.hide_conversation_in_email',
          false,
        );
      }
    },
    async deleteItem(idx) {
      if (!this.loading) {
        this.loading = true;
        this.items.splice(idx, 1);
        await this.updatePreferences();
        this.loading = false;
      }
    },
    async updatePreferences() {
      try {
        if (this.editableFirm.preferences) {
          this.editableFirm.preferences.customElements = this.items.join('');
          this.editableFirm.preferences.show_filing_instructions_to_clients = this.showFilingInstructionsToClients;
          this.editableFirm.preferences.require_one_signature_per_section = this.requireOneSignaturePerSection;
          this.editableFirm.preferences.disable_provided_elsewhere = this.disableProvidedElsewhere;
          this.editableFirm.preferences.disable_folder_action = this.disableFolderAction;
          this.editableFirm.preferences.questionnaire_document_notification_enabled = this.enableQuestionnaireDocumentNotification;
          this.editableFirm.preferences.disable_reupload_on_low_confidence = this.disableReuploadOnLowConfidence;
          this.editableFirm.preferences.hide_conversation_in_email = this.hideConversationInEmail;
        } else {
          this.editableFirm = {
            preferences: {
              customElements: this.items.join(''),
              show_filing_instructions_to_clients: this
                .showFilingInstructionsToClients,
              require_one_signature_per_section: this
                .requireOneSignaturePerSection,
              disable_provided_elsewhere: this.disableProvidedElsewhere,
              disable_folder_action: this.disableFolderAction,
              questionnaire_document_notification_enabled: this
                .enableQuestionnaireDocumentNotification,
              disable_reupload_on_low_confidence: this
                .disableReuploadOnLowConfidence,
              hide_conversation_in_email: this.hideConversationInEmail,
            },
          };
        }
        const response = await API.firm.updateCurrentUserPreferences(
          this.editableFirm,
        );
        if (response.data.error) {
          console.log(response.data);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was an issue.',
          });
        } else {
          this.updateCurrentFirm(this.editableFirm);
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was an issue.',
        });
      }
    },
  },
  created() {
    this.initList();
  },
};
</script>

<style></style>
